/**
 * Quick Add by SKU (/pages/quickadd, template page.skucart).
 *
 * Item numbers land in a list on the page. The Shopify cart is touched once,
 * when the shopper presses Add to cart. That keeps the wholesale drawer and
 * the retail Upnova cart from opening on every line.
 *
 * The SKU map comes from paginated /products.json because a Liquid paginate
 * loop can only expose one page (max 250) per render.
 */
import { CartAddEvent } from '@theme/events';

const PAGE_SIZE = 250;
const MAX_PAGES = 40;
const CACHE_KEY = 'clnf-sku-map-v2';
const LIST_KEY = 'clnf-sku-list-v1';
const CACHE_TTL_MS = 10 * 60 * 1000;
const SOURCE_ID = 'sku-quick-add';

/** @type {Map<string, SkuEntry>} */
const skuMap = new Map();
/** @type {Map<number, SkuEntry>} */
const variantById = new Map();
/** @type {Map<number, ListRow>} */
const list = new Map();

let catalogReady = false;
/** @type {Promise<void> | null} */
let catalogLoad = null;
let committing = false;

const shopRoot = () => window.Shopify?.routes?.root || '/';

function debug(...args) {
  if (window.__CLNF_DEBUG) console.log('[sku-quick-add]', ...args);
}

/**
 * @typedef {object} SkuEntry
 * @property {number} id
 * @property {string} sku
 * @property {string} title
 * @property {string} handle
 * @property {string} variantTitle
 * @property {string} image
 * @property {boolean} available
 * @property {number | null} price
 * @property {boolean} tracksInventory
 * @property {string} inventoryPolicy
 * @property {number | null} inventory
 */

/**
 * @typedef {object} ListRow
 * @property {SkuEntry} entry
 * @property {number} quantity
 * @property {string} note
 */

/** @type {HTMLElement | null} */
let root = null;

function currencyCode() {
  return root?.dataset.currency || 'USD';
}

function cartAddUrl() {
  const routes = window.Theme && Theme.routes;
  return (routes && routes.cart_add_url) || `${shopRoot()}cart/add.js`;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function itemLabel(count) {
  return count === 1 ? '1 item' : `${count} items`;
}

function parsePrice(value) {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function numberOrNull(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatMoney(amount) {
  if (amount == null || !Number.isFinite(amount)) return '';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: currencyCode(),
    }).format(amount);
  } catch (e) {
    return `$${amount.toFixed(2)}`;
  }
}

function sizedImage(src, width) {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.href;
  } catch (e) {
    return src;
  }
}

/** @param {SkuEntry} entry */
function maxPurchasable(entry) {
  if (!entry.available) return 0;
  if (!entry.tracksInventory || entry.inventoryPolicy === 'continue' || entry.inventory == null) return null;
  if (entry.inventory <= 0) return null;
  return entry.inventory;
}

function readyRows() {
  return [...list.values()].filter((row) => maxPurchasable(row.entry) !== 0);
}

/** @param {{ text: string, tone?: string }[]} notes */
function setStatus(notes) {
  const status = root?.querySelector('[data-status]');
  if (!status) return;
  status.replaceChildren();
  for (const note of notes) {
    const tone = note.tone && note.tone !== 'muted' ? ` quickadd__note--${note.tone}` : '';
    status.appendChild(el('p', `quickadd__note${tone}`, note.text));
  }
}

function clearStatus() {
  const status = root?.querySelector('[data-status]');
  if (status) status.replaceChildren();
}

function indexEntry(entry) {
  variantById.set(entry.id, entry);
  const sku = String(entry.sku).trim();
  if (!sku) return;
  skuMap.set(sku, entry);
  const lower = sku.toLowerCase();
  const upper = sku.toUpperCase();
  if (lower !== sku) skuMap.set(lower, entry);
  if (upper !== sku && upper !== lower) skuMap.set(upper, entry);
}

function lookupSku(sku) {
  const trimmed = String(sku || '').trim();
  if (!trimmed) return null;
  return skuMap.get(trimmed) || skuMap.get(trimmed.toLowerCase()) || skuMap.get(trimmed.toUpperCase()) || null;
}

function addVariantToMap(variant, product) {
  if (!variant || variant.sku == null || variant.id == null) return;
  const sku = String(variant.sku).trim();
  if (!sku) return;
  const productImage = product && product.images && product.images[0] && product.images[0].src;
  const variantImage = variant.featured_image && variant.featured_image.src;
  /** @type {SkuEntry} */
  const entry = {
    id: variant.id,
    sku,
    title: (product && product.title) || sku,
    handle: (product && product.handle) || '',
    variantTitle: variant.title && variant.title !== 'Default Title' ? String(variant.title) : '',
    image: variantImage || productImage || '',
    available: variant.available !== false,
    price: parsePrice(variant.price),
    tracksInventory: Boolean(variant.inventory_management),
    inventoryPolicy: variant.inventory_policy || '',
    inventory: numberOrNull(variant.inventory_quantity),
  };
  indexEntry(entry);
}

function addProductsToMap(products) {
  for (const product of products || []) {
    for (const variant of product.variants || []) addVariantToMap(variant, product);
  }
}

function readCache(expectedCount) {
  if (!expectedCount) return null;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (!parsed || parsed.count !== expectedCount || !Array.isArray(parsed.entries)) return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    if (!parsed.entries.length || !parsed.entries[0] || parsed.entries[0].id == null) return null;
    return parsed.entries;
  } catch (e) {
    return null;
  }
}

function writeCache(expectedCount) {
  if (!expectedCount || !variantById.size) return;
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        count: expectedCount,
        ts: Date.now(),
        entries: [...variantById.values()],
      })
    );
  } catch (e) {
    /* quota / private mode */
  }
}

function saveList() {
  try {
    const rows = [];
    list.forEach((row) => rows.push([row.entry.id, row.quantity]));
    sessionStorage.setItem(LIST_KEY, JSON.stringify(rows));
  } catch (e) {
    /* quota / private mode */
  }
}

function restoreList() {
  let saved = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(LIST_KEY) || 'null');
  } catch (e) {
    saved = null;
  }
  if (!Array.isArray(saved)) return;
  list.clear();
  for (const pair of saved) {
    if (!pair || pair.length < 2) continue;
    const entry = variantById.get(Number(pair[0]));
    const quantity = parseInt(pair[1], 10);
    if (!entry || !Number.isFinite(quantity) || quantity < 1) continue;
    const cap = maxPurchasable(entry);
    let qty = quantity;
    let note = '';
    if (cap != null && cap > 0 && qty > cap) {
      qty = cap;
      note = cap === 1 ? 'Only 1 in stock.' : `Only ${cap} in stock.`;
    }
    list.set(entry.id, { entry, quantity: cap === 0 ? 1 : qty, note });
  }
}

async function fetchPage(page) {
  const res = await fetch(`${shopRoot()}products.json?limit=${PAGE_SIZE}&page=${page}`, {
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`products.json ${res.status}`);
  const data = await res.json();
  return (data && data.products) || [];
}

function loadCatalog(expectedCount) {
  if (catalogReady && variantById.size) return Promise.resolve();
  if (catalogLoad) return catalogLoad;

  catalogLoad = (async () => {
    const cached = readCache(expectedCount);
    skuMap.clear();
    variantById.clear();
    if (cached && cached.length) {
      for (const entry of cached) indexEntry(entry);
      catalogReady = true;
      debug('cache hit', cached.length);
      return;
    }

    for (let page = 1; page <= MAX_PAGES; page++) {
      const count = root?.querySelector('[data-count]');
      if (count) count.textContent = `Loading products… (${page})`;
      const products = await fetchPage(page);
      addProductsToMap(products);
      if (products.length < PAGE_SIZE) break;
    }
    catalogReady = true;
    writeCache(expectedCount);
    debug('mapped', variantById.size, 'variants');
  })().finally(() => {
    catalogLoad = null;
  });

  return catalogLoad;
}

function parseSkuEntry(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return null;
  const dash = trimmed.lastIndexOf('-');
  if (dash > 0) {
    const qtyPart = trimmed.slice(dash + 1).trim();
    const qty = parseInt(qtyPart, 10);
    if (qtyPart !== '' && String(qty) === qtyPart && qty > 0) {
      return { sku: trimmed.slice(0, dash).trim(), quantity: qty };
    }
  }
  return { sku: trimmed, quantity: 1 };
}

/**
 * @param {SkuEntry} entry
 * @param {number} quantity
 */
function addToList(entry, quantity) {
  const cap = maxPurchasable(entry);
  const existing = list.get(entry.id);
  if (cap === 0) {
    if (!existing) list.set(entry.id, { entry, quantity: 1, note: '' });
    return 'oos';
  }
  let next = (existing ? existing.quantity : 0) + quantity;
  let note = '';
  if (cap != null && next > cap) {
    next = cap;
    note = cap === 1 ? 'Only 1 in stock.' : `Only ${cap} in stock.`;
  }
  list.set(entry.id, { entry, quantity: next, note });
  return note ? 'capped' : 'added';
}

function productHref(entry) {
  if (!entry.handle) return '';
  return `${shopRoot()}products/${encodeURIComponent(entry.handle)}?variant=${entry.id}`;
}

/**
 * @param {{ focusId?: number, focusKind?: string }} [options]
 */
function render(options) {
  if (!root) return;
  const rowsEl = root.querySelector('[data-rows]');
  const empty = root.querySelector('[data-empty]');
  const loading = root.querySelector('[data-loading]');
  const count = root.querySelector('[data-count]');
  const summary = root.querySelector('[data-summary]');
  const commit = root.querySelector('[data-commit]');
  const addButton = root.querySelector('[data-add]');
  if (!rowsEl || !empty || !loading || !count || !summary || !(commit instanceof HTMLButtonElement)) return;

  loading.hidden = catalogReady;
  if (addButton instanceof HTMLButtonElement) addButton.disabled = !catalogReady || committing;

  const rows = [...list.values()].sort((a, b) => {
    const ao = maxPurchasable(a.entry) === 0 ? 1 : 0;
    const bo = maxPurchasable(b.entry) === 0 ? 1 : 0;
    return ao - bo;
  });

  const ready = rows.filter((row) => maxPurchasable(row.entry) !== 0);
  const unavailable = rows.length - ready.length;
  const readyUnits = ready.reduce((sum, row) => sum + row.quantity, 0);
  const estimated = ready.reduce((sum, row) => {
    if (row.entry.price == null) return sum;
    return sum + row.entry.price * row.quantity;
  }, 0);
  const priced = ready.some((row) => row.entry.price != null);

  if (!catalogReady) {
    count.textContent = 'Loading products…';
  } else if (!rows.length) {
    count.textContent = 'Nothing yet';
  } else if (readyUnits < 1) {
    count.textContent = unavailable === 1 ? 'Out of stock' : `${unavailable} out of stock`;
  } else {
    count.textContent = itemLabel(readyUnits);
  }

  empty.hidden = !catalogReady || rows.length > 0;

  const parts = [];
  if (readyUnits > 0) parts.push(`${itemLabel(readyUnits)} ready`);
  if (priced && readyUnits > 0) parts.push(`Est. ${formatMoney(estimated)}`);
  if (unavailable > 0) {
    parts.push(unavailable === 1 ? '1 out of stock, not included' : `${unavailable} out of stock, not included`);
  }
  summary.textContent = parts.join(' · ');

  commit.disabled = committing || readyUnits < 1;
  commit.textContent = committing ? 'Adding…' : 'Add to cart';

  const fragment = document.createDocumentFragment();
  for (const row of rows) {
    fragment.appendChild(renderRow(row));
  }
  rowsEl.replaceChildren(fragment);

  if (options && options.focusId && options.focusKind) {
    const next = rowsEl.querySelector(`[data-focus="${options.focusKind}"][data-id="${options.focusId}"]`);
    if (next instanceof HTMLElement) next.focus();
    else if (options.focusKind === 'remove') root.querySelector('#querysku')?.focus();
  }
}

/** @param {ListRow} row */
function renderRow(row) {
  const entry = row.entry;
  const unavailable = maxPurchasable(entry) === 0;
  const article = el('article', `quickadd__row${unavailable ? ' is-unavailable' : ''}`);

  if (entry.image) {
    const img = document.createElement('img');
    img.className = 'quickadd__thumb';
    img.alt = '';
    img.width = 64;
    img.height = 64;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = sizedImage(entry.image, 128);
    article.appendChild(img);
  } else {
    article.appendChild(el('div', 'quickadd__thumb quickadd__thumb--empty'));
  }

  const body = el('div', 'quickadd__row-body');
  const top = el('div', 'quickadd__row-top');
  const heading = el('div');
  const href = productHref(entry);
  if (href) {
    const link = el('a', 'quickadd__title', entry.title);
    link.href = href;
    heading.appendChild(link);
  } else {
    heading.appendChild(el('p', 'quickadd__title', entry.title));
  }
  if (entry.variantTitle) heading.appendChild(el('span', 'quickadd__variant', entry.variantTitle));
  heading.appendChild(el('span', 'quickadd__sku', entry.sku));
  if (unavailable) heading.appendChild(el('span', 'quickadd__badge', 'Out of stock'));
  else if (row.note) heading.appendChild(el('span', 'quickadd__stock', row.note));

  const remove = el('button', 'quickadd__remove', 'Remove');
  remove.type = 'button';
  remove.setAttribute('data-remove', '');
  remove.setAttribute('data-id', String(entry.id));
  remove.setAttribute('data-focus', 'remove');

  top.append(heading, remove);
  body.appendChild(top);

  if (!unavailable) {
    const foot = el('div', 'quickadd__row-foot');
    const stepper = el('div', 'quickadd__stepper');
    const minus = el('button', '', '−');
    minus.type = 'button';
    minus.setAttribute('data-qty', '-1');
    minus.setAttribute('data-id', String(entry.id));
    minus.setAttribute('data-focus', 'minus');
    minus.setAttribute('aria-label', `Decrease quantity for ${entry.title}`);
    const input = document.createElement('input');
    input.type = 'number';
    input.min = '1';
    input.inputMode = 'numeric';
    input.value = String(row.quantity);
    input.setAttribute('data-qty-input', '');
    input.setAttribute('data-id', String(entry.id));
    input.setAttribute('data-focus', 'qty');
    input.setAttribute('aria-label', `Quantity for ${entry.title}`);
    const plus = el('button', '', '+');
    plus.type = 'button';
    plus.setAttribute('data-qty', '1');
    plus.setAttribute('data-id', String(entry.id));
    plus.setAttribute('data-focus', 'plus');
    plus.setAttribute('aria-label', `Increase quantity for ${entry.title}`);
    stepper.append(minus, input, plus);
    foot.appendChild(stepper);
    if (entry.price != null) foot.appendChild(el('p', 'quickadd__price', formatMoney(entry.price * row.quantity)));
    body.appendChild(foot);
  }

  article.appendChild(body);
  return article;
}

function changeQty(id, delta) {
  const row = list.get(id);
  if (!row || committing) return;
  const cap = maxPurchasable(row.entry);
  if (cap === 0) return;
  let next = row.quantity + delta;
  if (next < 1) next = 1;
  row.note = '';
  if (cap != null && next > cap) {
    next = cap;
    row.note = cap === 1 ? 'Only 1 in stock.' : `Only ${cap} in stock.`;
  }
  row.quantity = next;
  saveList();
  render({ focusId: id, focusKind: delta < 0 ? 'minus' : 'plus' });
}

function setQtyFromInput(id, raw) {
  const row = list.get(id);
  if (!row || committing) return;
  let next = parseInt(raw, 10);
  if (!Number.isFinite(next) || next < 1) next = 1;
  const cap = maxPurchasable(row.entry);
  row.note = '';
  if (cap === 0) {
    next = 1;
  } else if (cap != null && next > cap) {
    next = cap;
    row.note = cap === 1 ? 'Only 1 in stock.' : `Only ${cap} in stock.`;
  }
  row.quantity = next;
  saveList();
  render({ focusId: id, focusKind: 'qty' });
}

function submitToList() {
  if (!root || committing) return;
  const input = root.querySelector('#querysku');
  if (!(input instanceof HTMLInputElement)) return;
  const value = input.value.trim();
  if (!value) {
    setStatus([{ text: 'Enter an item number.', tone: 'bad' }]);
    return;
  }

  const run = () => {
    const entries = value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    /** @type {{ text: string, tone?: string }[]} */
    const notes = [];
    let recognized = 0;
    for (const raw of entries) {
      const parsed = parseSkuEntry(raw);
      if (!parsed || !parsed.sku) {
        notes.push({ text: `“${raw}” is not a valid item number.`, tone: 'bad' });
        continue;
      }
      const entry = lookupSku(parsed.sku);
      if (!entry) {
        notes.push({ text: `Item number ${parsed.sku} was not found.`, tone: 'bad' });
        continue;
      }
      recognized += 1;
      const result = addToList(entry, parsed.quantity);
      if (result === 'oos') {
        notes.push({
          text: `${entry.title} is out of stock. It stays in the list and will not be added to your cart.`,
          tone: 'warn',
        });
      } else if (result === 'capped') {
        const cap = maxPurchasable(entry);
        notes.push({
          text: `${entry.title} has ${cap === 1 ? '1 left' : `${cap} left`}. The quantity was lowered.`,
          tone: 'warn',
        });
      }
    }
    if (recognized > 0) {
      input.value = '';
      saveList();
    }
    if (!notes.length) clearStatus();
    else setStatus(notes);
    render();
    if (recognized > 0 && window.matchMedia('(min-width: 901px)').matches) {
      const rows = root.querySelector('[data-rows]');
      if (rows) rows.scrollTop = rows.scrollHeight;
    }
    input.focus();
  };

  if (catalogReady) {
    run();
    return;
  }
  const expectedCount = parseInt(root.getAttribute('data-product-count') || '', 10) || 0;
  loadCatalog(expectedCount).then(run, () => {
    setStatus([{ text: 'The product list did not load. Refresh and try again.', tone: 'bad' }]);
  });
}

function qtyMap(cart) {
  const map = new Map();
  for (const item of cart.items || []) {
    const id = item.variant_id;
    map.set(id, (map.get(id) || 0) + (Number(item.quantity) || 0));
  }
  return map;
}

function cartErrorMessage(data) {
  if (!data) return 'Could not add this item.';
  if (typeof data.description === 'string' && data.description) return data.description;
  if (typeof data.message === 'string' && data.message && data.message !== 'Cart Error') return data.message;
  if (typeof data.errors === 'string' && data.errors) return data.errors;
  return 'Could not add this item.';
}

async function postAdd(items) {
  const res = await fetch(cartAddUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ items }),
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    data = null;
  }
  return { ok: res.ok, data };
}

async function fetchCart() {
  const res = await fetch(`${shopRoot()}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
  if (!res.ok) throw new Error('Cart fetch failed');
  return res.json();
}

function updateHeaderCartCounts(cart) {
  if (!cart) return;
  const count = Number(cart.item_count) || 0;
  document.querySelectorAll('.cart-count, .cart-item-count-header--quantity, [data-cart-count], .cart-badge').forEach((node) => {
    node.textContent = String(count);
    if (count > 0) node.classList.remove('hide');
  });
  let total = '';
  try {
    total = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: cart.currency || currencyCode(),
    }).format((Number(cart.total_price) || 0) / 100);
  } catch (e) {
    total = `$${((Number(cart.total_price) || 0) / 100).toFixed(2)}`;
  }
  document.querySelectorAll('.cart-item-count-header--total').forEach((node) => {
    const money = node.querySelector('.money');
    if (money) money.textContent = total;
    else node.textContent = total;
    if (count > 0) node.classList.remove('hide');
  });
}

function isUpnovaOpen() {
  const host = document.getElementById('upezCart');
  if (!host) return false;
  const style = window.getComputedStyle(host);
  if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
  const rect = host.getBoundingClientRect();
  return rect.width > 8 && rect.height > 8;
}

function openCartOnce() {
  window.setTimeout(() => {
    const native = document.querySelector('cart-drawer-component');
    if (native && typeof native.open === 'function') {
      const dialog = native.querySelector('dialog');
      if (!dialog || !dialog.open) native.open();
      return;
    }
    if (!window.CLNF_useUpnovaMobileCart || isUpnovaOpen()) return;
    const trigger = document.querySelector('[data-upez-cart="upez"]');
    if (trigger instanceof HTMLElement) trigger.click();
  }, 250);
}

function publishCart(cart, shouldOpen) {
  const drawer = document.querySelector('cart-drawer-component');
  const restoreAuto = Boolean(drawer && !shouldOpen && drawer.hasAttribute('auto-open'));
  if (restoreAuto) drawer.removeAttribute('auto-open');
  document.dispatchEvent(
    new CartAddEvent(cart, SOURCE_ID, {
      source: SOURCE_ID,
      itemCount: cart.item_count,
    })
  );
  if (restoreAuto) drawer.setAttribute('auto-open', '');
  if (shouldOpen) openCartOnce();
}

async function commit() {
  if (committing || !root) return;
  const ready = readyRows();
  if (!ready.length) return;
  committing = true;
  render();
  setStatus([{ text: 'Adding to your cart…', tone: 'muted' }]);

  try {
    const before = qtyMap(await fetchCart());
    const items = ready.map((row) => ({ id: row.entry.id, quantity: row.quantity }));
    /** @type {Map<number, string>} */
    const errors = new Map();
    const batch = await postAdd(items);
    if (!batch.ok) {
      const mid = qtyMap(await fetchCart());
      const batchAddedAny = items.some((item) => (mid.get(item.id) || 0) > (before.get(item.id) || 0));
      const message = cartErrorMessage(batch.data);
      if (!batchAddedAny && items.length > 1) {
        for (const item of items) {
          const one = await postAdd([item]);
          if (!one.ok) errors.set(item.id, cartErrorMessage(one.data));
        }
      } else {
        for (const item of items) {
          if ((mid.get(item.id) || 0) <= (before.get(item.id) || 0)) errors.set(item.id, message);
        }
      }
    }

    const cart = await fetchCart();
    const after = qtyMap(cart);
    let addedUnits = 0;
    for (const row of ready) {
      const delta = (after.get(row.entry.id) || 0) - (before.get(row.entry.id) || 0);
      if (delta >= row.quantity) {
        list.delete(row.entry.id);
        addedUnits += row.quantity;
      } else if (delta > 0) {
        row.quantity -= delta;
        addedUnits += delta;
        row.note = errors.get(row.entry.id) || `Added ${delta}. The rest is still in the list.`;
      } else {
        row.note = errors.get(row.entry.id) || 'Could not add this item.';
      }
    }
    saveList();

    const leftovers = readyRows().length > 0;
    if (addedUnits > 0) {
      updateHeaderCartCounts(cart);
      publishCart(cart, !leftovers);
    }
    if (addedUnits > 0 && !leftovers) {
      setStatus([{ text: `Added ${itemLabel(addedUnits)} to your cart.`, tone: 'ok' }]);
    } else if (addedUnits > 0) {
      setStatus([
        {
          text: `Added ${itemLabel(addedUnits)} to your cart. What could not be added stayed in the list.`,
          tone: 'warn',
        },
      ]);
    } else {
      setStatus([{ text: 'Nothing was added. Check the notes in the list and try again.', tone: 'bad' }]);
    }
  } catch (error) {
    debug('commit failed', error);
    setStatus([{ text: 'Could not reach the cart. Check your connection and try again.', tone: 'bad' }]);
  } finally {
    committing = false;
    render();
  }
}

function init() {
  root = document.getElementById('clnf-sku-quick-add');
  if (!root) return;

  const form = root.querySelector('[data-form]');
  const input = root.querySelector('#querysku');
  if (!(form instanceof HTMLFormElement) || !(input instanceof HTMLInputElement)) return;

  const expectedCount = parseInt(root.getAttribute('data-product-count') || '', 10) || 0;

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    submitToList();
  });
  input.addEventListener('input', () => {
    if (root?.querySelector('[data-status]')?.childNodes.length) clearStatus();
  });

  root.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const qty = target.closest('[data-qty]');
    if (qty) {
      changeQty(Number(qty.getAttribute('data-id')), Number(qty.getAttribute('data-qty')));
      return;
    }
    const remove = target.closest('[data-remove]');
    if (remove) {
      list.delete(Number(remove.getAttribute('data-id')));
      saveList();
      render({ focusId: Number(remove.getAttribute('data-id')), focusKind: 'remove' });
      return;
    }
    if (target.closest('[data-commit]')) commit();
  });

  root.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-qty-input')) return;
    setQtyFromInput(Number(target.getAttribute('data-id')), target.value);
  });

  loadCatalog(expectedCount)
    .then(() => {
      restoreList();
      render();
    })
    .catch((err) => {
      debug('catalog load failed', err);
      catalogReady = false;
      render();
      setStatus([{ text: 'The product list did not load. Refresh and try again.', tone: 'bad' }]);
    });

  render();
}

init();
